import { FC, useMemo } from 'react';

import { CompanyStatus } from '@apis/companiesApi.types';
import { useOptions } from '@core/contexts/OptionsContext';
import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { InvestorProfileFormData } from '@modules/crm/components/investor-profile/InvestorProfile';
import { Badge, BADGE_THEME } from '@shared/components/badge';
import { Flex } from '@shared/components/flex';
import { SingleGroupSelect } from '@shared/components/group-select-new/single-group-select';
import { MultiSelect } from '@shared/components/select-new/multi-select';
import { SingleSelect } from '@shared/components/select-new/single-select';
import { TextField } from '@shared/components/text-field';
import { CompanyTypes } from '@shared/models/company/company';
import { ButtonSize } from '@shared/types/common/button';
import { InputSize } from '@shared/types/common/input';

import { styles } from './CompanyDetailsForm.styles';

export interface CompanyDetailsFormData extends InvestorProfileFormData {
  alias: string;
  companyNumber: string;
  companySource: string;
  companyType: CompanyTypes[];
  description: string;
  dunsNumber: string;
  email: string;
  name: string;
  phoneNumber: string;
  status: string;
  taxId: string;
  website: string;
  hasInvestorProfile: boolean;
}

export type CompanyDetailsFormErrors = Record<keyof CompanyDetailsFormData, string>;

export interface CompanyDetailsFormProps extends WithStyles<typeof styles> {
  disabled?: boolean;
  dunsNumberVisible?: boolean;
  errors: SuppliedErrors<CompanyDetailsFormErrors>;
  formData: SuppliedData<CompanyDetailsFormData>;
  nameDisabled?: boolean;
  newName?: boolean;
  statusVisible?: boolean;
  onNameBlur?: () => void;
  onNameFocus?: () => void;
  setFormData: (data: SuppliedData<CompanyDetailsFormData>) => void;
}

const CompanyDetailsFormComponent: FC<CompanyDetailsFormProps> = ({
  classes,
  disabled = false,
  dunsNumberVisible = false,
  errors,
  formData,
  nameDisabled = false,
  newName = false,
  statusVisible = false,
  onNameBlur,
  onNameFocus,
  setFormData,
}) => {
  const { companyStatusOptions, companyTypeOptions, preferredSourceOptions } = useOptions();

  const sourceGroupOptions = useMemo(() => [{ label: '', options: preferredSourceOptions }], [preferredSourceOptions]);

  const handleFormChange: ChangeCallback<CompanyDetailsFormData> = (key) => (value) => {
    setFormData({ ...formData, [key]: value });
  };

  return (
    <div>
      <Flex alignItems="baseline" wrap="nowrap" classes={{ root: classes.row }} justifyContent="space-between">
        <div className={classes.rowElement}>
          <span className={classes.label}>{t('xtextx_asterisk_1', { text: t('company_name') })}</span>
          <TextField
            disabled={nameDisabled || disabled}
            error={!!errors.name}
            errorText={errors.name}
            fullWidth
            InputProps={{
              startAdornment: newName ? (
                <Badge text={t('new')} theme={BADGE_THEME.green} classes={{ badge: classes.newBadge }} />
              ) : null,
            }}
            placeholder={t('eg_xtextx', { text: 'Kato' })}
            size={InputSize.medium}
            value={formData.name}
            onChange={(e) => handleFormChange('name')(e.target.value)}
            onBlur={onNameBlur}
            onFocus={onNameFocus}
          />
        </div>
        <div className={classes.rowElement}>
          <span className={classes.label}>{t('alias')}</span>
          <TextField
            disabled={disabled}
            fullWidth
            error={!!errors.alias}
            errorText={errors.alias}
            placeholder={t('eg_xtextx', { text: 'Kato Ltd' })}
            size={InputSize.medium}
            value={formData.alias}
            onChange={(e) => handleFormChange('alias')(e.target.value)}
          />
        </div>
      </Flex>

      <Flex alignItems="center" wrap="nowrap" classes={{ root: classes.row }} justifyContent="space-between">
        <div className={classes.rowElement}>
          <span className={classes.label}>{t('website')}</span>
          <TextField
            disabled={disabled}
            error={!!errors.website}
            errorText={errors.website}
            fullWidth
            placeholder={t('eg_xtextx', { text: 'kato.app' })}
            size={InputSize.medium}
            value={formData.website}
            onChange={(e) => handleFormChange('website')(e.target.value)}
          />
        </div>
        <div className={classes.rowElement}>
          <span className={classes.label}>{t('company_email')}</span>
          <TextField
            disabled={disabled}
            error={!!errors.email}
            errorText={errors.email}
            fullWidth
            placeholder={t('eg_xtextx', { text: 'hello@kato.app' })}
            size={InputSize.medium}
            value={formData.email}
            onChange={(e) => handleFormChange('email')(e.target.value)}
          />
        </div>
      </Flex>

      <Flex alignItems="center" wrap="nowrap" classes={{ root: classes.row }} justifyContent="space-between">
        <div className={classes.rowElement}>
          <span className={classes.label}>{t('phone_number')}</span>
          <TextField
            disabled={disabled}
            error={!!errors.phoneNumber}
            errorText={errors.phoneNumber}
            fullWidth
            placeholder={t('eg_xtextx', { text: '+44 (0)118 389 239' })}
            value={formData.phoneNumber}
            onChange={(e) => handleFormChange('phoneNumber')(e.target.value)}
          />
        </div>
        <div className={classes.rowElement}>
          <span className={classes.label}>{t('company_number')}</span>
          <TextField
            disabled={disabled}
            error={!!errors.companyNumber}
            errorText={errors.companyNumber}
            fullWidth
            placeholder={t('eg_xtextx', { text: 'AB123456' })}
            size={InputSize.medium}
            value={formData.companyNumber}
            onChange={(e) => handleFormChange('companyNumber')(e.target.value)}
          />
        </div>
      </Flex>

      {dunsNumberVisible && (
        <Flex alignItems="center" wrap="nowrap" classes={{ root: classes.row }} justifyContent="space-between">
          <div className={classes.rowElement}>
            <span className={classes.label}>{t('duns_number')}</span>
            <TextField
              disabled={true}
              error={!!errors.dunsNumber}
              errorText={errors.dunsNumber}
              fullWidth
              placeholder={t('eg_xtextx', { text: '123456' })}
              size={InputSize.medium}
              value={formData.dunsNumber}
              onChange={(e) => handleFormChange('dunsNumber')(e.target.value)}
            />
          </div>
        </Flex>
      )}

      <Flex alignItems="center" wrap="nowrap" classes={{ root: classes.row }} justifyContent="space-between">
        <div className={classes.rowElement}>
          <span className={classes.label}>{t('tax_id')}</span>
          <TextField
            disabled={disabled}
            error={!!errors.taxId}
            errorText={errors.taxId}
            fullWidth
            placeholder={t('eg_xtextx', { text: 'GB123456789' })}
            size={InputSize.medium}
            value={formData.taxId}
            onChange={(e) => handleFormChange('taxId')(e.target.value)}
          />
        </div>

        <div className={classes.rowElement}>
          <span className={classes.label}>{t('type')}</span>
          <MultiSelect
            classes={{ root: classes.selectRoot }}
            disabled={disabled}
            error={!!errors.companyType}
            errorText={errors.companyType}
            options={companyTypeOptions}
            placeholder={t('xtextx_ellipsis', { text: t('please_select') })}
            size={ButtonSize.medium}
            value={formData.companyType}
            onChange={(value) => handleFormChange('companyType')(value as CompanyTypes[])}
          />
        </div>
      </Flex>

      <Flex alignItems="center" wrap="nowrap" classes={{ root: classes.row }} justifyContent="space-between">
        <div className={classes.rowElement}>
          <span className={classes.label}>{t('source_of_company')}</span>
          <SingleGroupSelect
            classes={{ root: classes.selectRoot }}
            disabled={disabled}
            error={!!errors.companySource}
            errorText={errors.companySource}
            groupOptions={sourceGroupOptions}
            placeholder={t('xtextx_ellipsis', { text: t('please_select') })}
            size={ButtonSize.medium}
            value={formData.companySource}
            onChange={(value) => handleFormChange('companySource')(value as string)}
          />
        </div>
        {statusVisible && (
          <div className={classes.rowElement}>
            <span className={classes.label}>{t('status')}</span>
            {/* TODO: Fix typing, should be one type only */}
            <SingleSelect<CompanyStatus | string>
              classes={{ root: classes.selectRoot }}
              disabled={disabled}
              error={!!errors.status}
              errorText={errors.status}
              options={companyStatusOptions}
              placeholder={t('xtextx_ellipsis', { text: t('please_select') })}
              size={ButtonSize.medium}
              value={formData.status}
              onChange={(value) => setFormData({ ...formData, status: value as string })}
            />
          </div>
        )}
      </Flex>

      <Flex alignItems="flex-start" direction="column" classes={{ root: classes.row }} justifyContent="space-between">
        <span className={classes.label}>{t('company_summary')}</span>
        <TextField
          disabled={disabled}
          error={!!errors.description}
          errorText={errors.description}
          multiline={true}
          maxRows={3}
          fullWidth
          placeholder={t('xtextx_ellipsis', { text: t('enter_a_summary_of_the_company') })}
          size={InputSize.medium}
          value={formData.description}
          onChange={(e) => handleFormChange('description')(e.target.value)}
        />
      </Flex>
    </div>
  );
};

export const CompanyDetailsForm = withStyles(styles)(CompanyDetailsFormComponent);
